import * as React from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import MovingIcon from "@mui/icons-material/Moving";
import LensIcon from "@mui/icons-material/Lens";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import CallMade from "@mui/icons-material/CallMade";
import MouseIcon from "@mui/icons-material/Mouse";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import cornerstoneTools from "cornerstone-tools";
import cornerstone from "cornerstone-core";
import { useSelector, useDispatch } from "react-redux";
import { addTools, isObjectInArray } from "./Viewers/utils";
import FormatPaintIcon from "@mui/icons-material/FormatPaint";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import BrushToolSettings from "./Settings/BrushTool";
import AddRoadIcon from "@mui/icons-material/AddRoad";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import { TbOvalVertical } from "react-icons/tb";
import { FiTarget } from "react-icons/fi";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { Alert, Button, IconButton } from "@mui/material";
import { useEffect } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";

import {
  Etdrs_Segmentation_Api_Call,
  Etdrs_Volumes_Api_Call,
  getOpCornerstoneElement,
  buildVolumesSaveParams,
  Submit_Lison_annotations,
  extractAiSegmentationData,
  extractSegmentCurvePixels,
  extractPointsCoordinates,
  generateMlPayload,
  get_annotations,
  sendFileUpload,
} from "./actions/dcm_actions";
import {
  resetFluidFrameToBaseline,
  snapshotFluidBaselinesForStack,
} from "./utils/fluidFrameState";

import { toPng } from "html-to-image";
import { CircularProgress } from "@mui/material";
import EtdrsConfirmationModal from "../../Components/EtdrsConfirmationModal";
import ScreenshotDownloadDialog from "./Dialogs/ScreenshotDownloadDialog";
import ScreenshotProgressSnackbar from "../../Components/ScreenshotProgressSnackbar";
import {
  downloadScreenshotByType,
  SCREENSHOT_TYPES,
} from "./utils/screenshotUtils";
import { API_URL } from "../../Endpoints";

import {
  SEGMENTATION_ERASER_MODE,
  setSegmentationEraserMode,
  syncAiSegmentationEraseDiameter,
  syncFluidEraseDiameter,
} from "./tools/AiSegmentationEraser";

const FLUID_MANUAL_TOOLS = new Set(["Paint", "FluidSegmentationEraser"]);

const FLUID_TOOL_BUTTON_SX = {
  py: 0.2,
  px: 0.6,
  minWidth: 28,
  height: 28,
  "& svg": { fontSize: "1.05rem" },
};

function AiSegmentationEraserIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="24px"
      viewBox="0 -960 960 960"
      width="24px"
      fill="currentColor"
      style={{ display: "inline-block", verticalAlign: "middle" }}
    >
      <path d="M690-240h190v80H610l80-80Zm-500 80-85-85q-23-23-23.5-57t22.5-58l440-456q23-24 56.5-24t56.5 23l199 199q23 23 23 57t-23 57L520-160H190Zm296-80 314-322-198-198-442 456 64 64h262Zm-6-240Z" />
    </svg>
  );
}

/** Hollow circle — matches fluid eraser cursor ring */
function FluidEraserIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="18px"
      viewBox="0 0 24 24"
      width="18px"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ display: "inline-block", verticalAlign: "middle" }}
    >
      <circle cx="12" cy="12" r="7" />
      <path
        d="M8 17 L16 7"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  );
}

// Function to extract AiSegmentation data and rename URIs to frame0, frame1, etc.

// Function to download JSON data
const downloadJsonFile = (data, filename = "ai_segmentation_data.json") => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

let prevSs = "";

const checkObjectInPossiblyNestedArray = (array, target) => {
  if (!array || !Array.isArray(array)) return false;

  // If first element is an array, we have a nested array
  if (Array.isArray(array[0])) {
    return array.some((subArray) =>
      checkObjectInPossiblyNestedArray(subArray, target),
    );
  }

  // Normal flat array check
  return isObjectInArray(array, target);
};

const hasSegment = (array, target) =>
  checkObjectInPossiblyNestedArray(array, target) ||
  isObjectInArray(array, target);

const createJoinedSegmentLine = (point) => ({
  active: false,
  canComplete: false,
  handles: {
    boundingBox: {
      height: 25,
      left: 5,
      top: 117,
      width: 50,
    },
    drawnIndependently: true,
    hasBoundingBox: true,
    hasMoved: false,
    movesIndependently: false,
    points: [
      {
        lines: [
          {
            x: point?.end?.x,
            y: point?.end?.y,
          },
        ],
        x: point?.start?.x,
        y: point?.start?.y,
      },
      {
        lines: [],
        x: point?.end?.x,
        y: point?.end?.y,
      },
    ],
    x: point?.start?.x,
    y: point?.start?.y,
  },
  name: "Segment",
  label: point?.label,
  visible: true,
});

const getFrameIdFromImageId = (imageId) => imageId?.split("?").pop();

const getToolStateForImageId = (toolStateManager, imageId, toolName) => {
  if (!toolStateManager || !imageId) {
    return null;
  }

  if (typeof toolStateManager.getImageIdToolState === "function") {
    const toolState = toolStateManager.getImageIdToolState(imageId, toolName);
    if (toolState) {
      return toolState;
    }
  }

  if (typeof toolStateManager.getToolState === "function") {
    return toolStateManager.getToolState(imageId, toolName);
  }

  return null;
};

const lineToolStateHasJoinedSegment = (lineToolState, target) =>
  Boolean(
    lineToolState?.data?.some(
      (tool) =>
        tool?.name === "Segment" &&
        tool?.label === target?.label &&
        tool?.handles?.points?.[0]?.x === target?.start?.x &&
        tool?.handles?.points?.[0]?.y === target?.start?.y &&
        tool?.handles?.points?.[1]?.x === target?.end?.x &&
        tool?.handles?.points?.[1]?.y === target?.end?.y,
    ),
  );

const getFrameIndexFromSegment = (point, fallbackFrameIndex) => {
  const frameIndex =
    point?.frameIndex ?? point?.currentFrame ?? point?.frame ?? fallbackFrameIndex;
  const parsed = Number(frameIndex);

  return Number.isInteger(parsed) ? parsed : fallbackFrameIndex;
};

const mergeUniquePoints = (points, pointsToAdd) => {
  const seen = new Set();
  const merged = [];

  [...(points || []), ...(pointsToAdd || [])].forEach((point) => {
    if (point?.x == null || point?.y == null) {
      return;
    }

    const normalizedPoint = {
      x: Math.round(point.x),
      y: Math.round(point.y),
    };
    const key = `${normalizedPoint.x},${normalizedPoint.y}`;

    if (!seen.has(key)) {
      seen.add(key);
      merged.push(normalizedPoint);
    }
  });

  return merged.sort((a, b) => a.x - b.x);
};

const clearAiSegmentationEraseMetadata = (segment) => {
  delete segment.originalPoints;
  delete segment.pointStatus;
  delete segment.erasedPointIndices;
  delete segment.erasedSegments;
  delete segment.erasedSegmentsInfo;
  delete segment.erasedLines;
};

const freezeJoinedSegmentsInToolState = (lineToolState, aiToolState, dicomData) => {
  const lineData = lineToolState?.data;
  const aiData = aiToolState?.data;

  if (!Array.isArray(lineData) || !Array.isArray(aiData)) {
    return false;
  }

  const remainingLines = [];
  let changed = false;

  lineData.forEach((line) => {
    if (line?.name !== "Segment") {
      remainingLines.push(line);
      return;
    }

    const curvePixels = extractSegmentCurvePixels(line);
    const aiSegment = aiData.find((segment) => segment?.label === line?.label);

    if (
      !aiSegment ||
      !Array.isArray(curvePixels) ||
      curvePixels.length === 0
    ) {
      remainingLines.push(line);
      return;
    }

    aiSegment.points = mergeUniquePoints(aiSegment.points, curvePixels);
    aiSegment.visible = dicomData?.settings?.Segmentation?.aiSegmentation;
    aiSegment.active = aiSegment.active !== false;
    aiSegment.invalidated = true;
    clearAiSegmentationEraseMetadata(aiSegment);
    changed = true;
  });

  if (changed) {
    lineToolState.data = remainingLines;
  }

  return changed;
};

const freezeSubmittedJoinedSegments = (dicomData, stackId) => {
  const imageIds = dicomData?.dicom_data?.[stackId]?.OPT?.stack?.imageIds || [];
  const toolStateManager =
    cornerstoneTools.globalImageIdSpecificToolStateManager;
  const currentElement = dicomData?.dicomRefBscan?.current;
  const currentFrame = dicomData?.currentFrame;

  if (
    !toolStateManager?.saveImageIdToolState ||
    !toolStateManager?.restoreImageIdToolState
  ) {
    return false;
  }

  let changed = false;

  imageIds.forEach((imageId, frameIndex) => {
    if (frameIndex === currentFrame && currentElement) {
      const frameChanged = freezeJoinedSegmentsInToolState(
        cornerstoneTools.getToolState(currentElement, "Line"),
        cornerstoneTools.getToolState(currentElement, "AiSegmentation"),
        dicomData,
      );

      if (frameChanged) {
        changed = true;
      }
    }

    const imageToolState = toolStateManager.saveImageIdToolState(imageId);
    const frameChanged = freezeJoinedSegmentsInToolState(
      imageToolState?.Line,
      imageToolState?.AiSegmentation,
      dicomData,
    );

    if (frameChanged) {
      toolStateManager.restoreImageIdToolState(imageId, imageToolState);
      changed = true;
    }
  });

  return changed;
};

const activateJoinedSegmentEditMode = (dicomData) => {
  const bscanEl = dicomData?.dicomRefBscan?.current;
  const opEl = dicomData?.OpRef?.current || dicomData?.OpCornerstoneRef?.current;
  const LineTool = cornerstoneTools.LineTool;

  cornerstoneTools.joinedSegmentEditOnly = true;

  if (LineTool) {
    LineTool.prototype.configuration = {
      ...LineTool.prototype.configuration,
      joinedSegmentEditOnly: true,
    };
    cornerstoneTools.addTool(LineTool);
  }

  try {
    if (opEl && typeof cornerstoneTools.setToolPassiveForElement === "function") {
      cornerstoneTools.setToolPassiveForElement(opEl, "Line");
    }
  } catch (_e) {
    /* noop */
  }

  if (
    bscanEl &&
    typeof cornerstoneTools.setToolActiveForElement === "function"
  ) {
    cornerstoneTools.setToolActiveForElement(bscanEl, "Line", {
      mouseButtonMask: 1,
    });
    bscanEl.style.cursor = "auto";
    return;
  }

  cornerstoneTools.setToolActive("Line", { mouseButtonMask: 1 });
  if (bscanEl) {
    bscanEl.style.cursor = "auto";
  }
};

const disableJoinedSegmentEditMode = () => {
  cornerstoneTools.joinedSegmentEditOnly = false;

  const LineTool = cornerstoneTools.LineTool;
  if (LineTool) {
    LineTool.prototype.configuration = {
      ...LineTool.prototype.configuration,
      joinedSegmentEditOnly: false,
    };
  }
};

export default function ToolButtons({ props }) {
  const [etdrsConfirm, setEtdrsConfirm] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const [submiting, setsubmitting] = React.useState(false);
  const [imageReceieved, setImageReceieved] = React.useState(true);
  const [imageDownloadPressed, setImageDownloadPressed] = React.useState(false);
  const [isEtdrsActive, setIsEtdrsActive] = React.useState(false);
  const [tabRestoreScreenshot, setTabRestoreScreenshot] = React.useState(false);
  const [screenshotDialogOpen, setScreenshotDialogOpen] = React.useState(false);
  const [screenshotDownloading, setScreenshotDownloading] =
    React.useState(false);
  const [screenshotProgress, setScreenshotProgress] = React.useState({
    open: false,
    message: "",
    current: 0,
    total: 0,
    etaSeconds: null,
    phase: "capturing",
  });

  const [file, setFile] = React.useState(null);
  const dispatch = useDispatch();
  let { search } = useLocation();

  const query = new URLSearchParams(search);
  const dicom_data = useSelector((state) => state.dcm);
  const etdrsActive = useSelector((state) => state.dcm).etdrsActive;

  // Handler for downloading AiSegmentation data
  const handleDownloadAiSegmentation = () => {
    try {
      const etdrsValuesFromSegmentationApi = extractAiSegmentationData(
        dicom_data?.dicom_data,
        dicom_data?.drawingTools,
        dicom_data?.opMetaData,
        dicom_data?.optMetaData,
        query.get("stack_id"),
      );

      if (Object.keys(etdrsValuesFromSegmentationApi).length > 0) {
        downloadJsonFile(
          etdrsValuesFromSegmentationApi,
          "etdrs_values_from_segmentation_api.json",
        );
      } else {
        alert("No AiSegmentation data found to download.");
      }
    } catch (error) {
      console.error("Error downloading AiSegmentation data:", error);
      alert("Error occurred while preparing download.");
    }
  };

  useEffect(() => {
    const imageIds =
      dicom_data.dicom_data[query.get("stack_id")]?.OPT?.stack?.imageIds || [];
    const toolStateManager =
      cornerstoneTools.globalImageIdSpecificToolStateManager;
    const nextSegmentsToDraw = {};

    imageIds.forEach((imageId, fallbackFrameIndex) => {
      const frameId = getFrameIdFromImageId(imageId);
      const aiToolState =
        getToolStateForImageId(toolStateManager, imageId, "AiSegmentation") ||
        (fallbackFrameIndex === dicom_data?.currentFrame
          ? dicom_data.dicom_data[query.get("stack_id")]?.OPT?.annotations
              ?.AiSegmentation
          : null);
      const lineToolState = getToolStateForImageId(
        toolStateManager,
        imageId,
        "Line",
      );

      aiToolState?.data?.forEach((segment) => {
        segment?.erasedSegmentsInfo?.forEach((point) => {
          const frameIndex = getFrameIndexFromSegment(point, fallbackFrameIndex);

          if (frameIndex !== fallbackFrameIndex) {
            return;
          }

          const toCheck = {
            start: point?.start,
            end: point?.end,
            label: segment?.label,
            frameIndex,
            frameId,
          };

          if (
            !toCheck.start ||
            !toCheck.end ||
            hasSegment(dicom_data.drawnSegments[frameIndex], toCheck) ||
            hasSegment(nextSegmentsToDraw[frameIndex], toCheck) ||
            lineToolStateHasJoinedSegment(lineToolState, toCheck)
          ) {
            return;
          }

          nextSegmentsToDraw[frameIndex] = [
            ...(nextSegmentsToDraw[frameIndex] || []),
            toCheck,
          ];
        });
      });
    });

    dispatch({
      type: "SET_SEGMENTS_TO_DRAW",
      SegmentsToDraw: nextSegmentsToDraw,
    });
    // // Use the utility function to add and activate the tool
  }, [
    dicom_data.dicom_data[query.get("stack_id")]?.OPT?.annotations,
    dicom_data.dicom_data[query.get("stack_id")]?.OPT?.stack?.imageIds,
  ]);
  const joinErasedSegments = () => {
    const imageIds =
      dicom_data.dicom_data[query.get("stack_id")]?.OPT?.stack?.imageIds || [];
    const toolStateManager =
      cornerstoneTools.globalImageIdSpecificToolStateManager;
    const nextDrawnSegments = { ...dicom_data.drawnSegments };
    const framesToClear = {};

    activateJoinedSegmentEditMode(dicom_data);
    dispatch({ type: "CHANGE_SELECTED_TOOL", payload: "Line" });

    Object.entries(dicom_data?.SegmentsToDraw || {}).forEach(
      ([frameIndexKey, points]) => {
        const frameIndex = Number(frameIndexKey);
        const imageId = imageIds[frameIndex];

        if (!imageId || !Array.isArray(points) || points.length === 0) {
          return;
        }

        const isCurrentFrame = frameIndex === dicom_data?.currentFrame;
        const imageToolState =
          !isCurrentFrame && toolStateManager?.saveImageIdToolState
            ? toolStateManager.saveImageIdToolState(imageId) || {}
            : null;

        if (
          !isCurrentFrame &&
          (!imageToolState || !toolStateManager?.restoreImageIdToolState)
        ) {
          return;
        }

        const lineToolState = isCurrentFrame
          ? cornerstoneTools.getToolState(
              dicom_data?.dicomRefBscan?.current,
              "Line",
            )
          : imageToolState?.Line || { data: [] };

        if (lineToolState && !lineToolState.handles) {
          lineToolState.handles = true;
        }

        points.forEach((point) => {
          if (
            hasSegment(nextDrawnSegments[frameIndex], point) ||
            lineToolStateHasJoinedSegment(lineToolState, point)
          ) {
            return;
          }

          const joinedSegment = createJoinedSegmentLine(point);

          if (isCurrentFrame) {
            cornerstoneTools.addToolState(
              dicom_data?.dicomRefBscan?.current,
              "Line",
              joinedSegment,
            );
          } else {
            imageToolState.Line = imageToolState.Line || { data: [] };
            imageToolState.Line.handles = true;
            imageToolState.Line.data = imageToolState.Line.data || [];
            imageToolState.Line.data.push(joinedSegment);
          }

          nextDrawnSegments[frameIndex] = [
            ...(nextDrawnSegments[frameIndex] || []),
            point,
          ];
        });

        if (!isCurrentFrame && toolStateManager?.restoreImageIdToolState) {
          toolStateManager.restoreImageIdToolState(imageId, imageToolState);
        }

        framesToClear[frameIndex] = [];
      },
    );

    if (dicom_data?.dicomRefBscan?.current) {
      cornerstone.updateImage(dicom_data.dicomRefBscan.current);
    }

    dispatch({
      type: "SET_DRAWN_SEGMENTS",
      drawnSegments: nextDrawnSegments,
    });
    dispatch({
      type: "SET_SEGMENTS_TO_DRAW",
      SegmentsToDraw: {
        ...dicom_data.SegmentsToDraw,
        ...framesToClear,
      },
    });
    // // Use the utility function to add and activate the tool
    //
  };
  const activateSegmentationEraserForMode = (mode) => {
    cornerstoneTools.segmentationEraseMode = mode;
    setSegmentationEraserMode(mode);
    dispatch({
      type: "SET_SEGMENTATION_ERASER_MODE",
      mode: mode === SEGMENTATION_ERASER_MODE.FLUID ? "fluid" : "ai",
    });
    try {
      // OCT init leaves several tools ACTIVE with LMB; they can eat events before eraser runs.
      const overlayToolsPassive = [
        "Wwwc",
        "RectangleStatic",
        "LineStatic",
        "AiSegmentation",
        "ThicknessMap",
        "DragRL",
      ];
      overlayToolsPassive.forEach((toolName) => {
        try {
          cornerstoneTools.setToolPassive(toolName);
        } catch (e) {
          /* noop */
        }
      });

      const activeTools = cornerstoneTools.getActiveTools?.();
      if (activeTools && activeTools.length > 0) {
        activeTools.forEach((tool) => {
          const name = typeof tool === "string" ? tool : tool?.name;
          if (name && name !== "AiSegmentationEraser") {
            try {
              cornerstoneTools.setToolPassive(name);
            } catch (e) {
              /* noop */
            }
          }
        });
      }

      try {
        cornerstoneTools.removeTool("AiSegmentationEraser");
      } catch (e) {
        console.log("Tool not found to remove:", e);
      }

      const brushState = dicom_data?.settings?.brush;

      const EraserCtor = cornerstoneTools.AiSegmentationEraser;
      if (EraserCtor) {
        EraserCtor.prototype.svgCursor = null;
        const aiBrushPx =
          brushState?.brushSize || brushState?.borderRadius || 20;
        const fluidBrushPx = brushState?.fluidEraserDiameter ?? aiBrushPx;
        if (mode === SEGMENTATION_ERASER_MODE.FLUID) {
          syncFluidEraseDiameter(fluidBrushPx);
        } else {
          syncAiSegmentationEraseDiameter(aiBrushPx);
        }

        EraserCtor.prototype.configuration = {
          brushSize:
            mode === SEGMENTATION_ERASER_MODE.FLUID ? fluidBrushPx : aiBrushPx,
          brushColor: "red",
          alpha: 0.4,
        };

        cornerstoneTools.addTool(EraserCtor);
        const bscanEl = dicom_data?.dicomRefBscan?.current;
        const eraserOpts = { mouseButtonMask: 1 };
        if (
          typeof cornerstoneTools.setToolActiveForElement === "function" &&
          bscanEl
        ) {
          cornerstoneTools.setToolActiveForElement(
            bscanEl,
            "AiSegmentationEraser",
            eraserOpts,
          );
        } else {
          cornerstoneTools.setToolActive("AiSegmentationEraser", eraserOpts);
        }
      }

      const scanEl =
        dicom_data?.dicomRefBscan?.current || dicom_data?.OpRef?.current;
      if (scanEl) {
        cornerstone.updateImage(scanEl);
      }
    } catch (error) {
      console.error("Error activating tool:", error);
    }
  };

  const handleToggle = () => {
    setIsEtdrsActive(!isEtdrsActive); // Toggle the etdrsActive state
    dispatch({
      type: "makeEtdrsActive_changed",
      makeEtdrsActive: false,
    });
    dispatch({
      type: "isEtdrsActive_changed",
      isEtdrsActive: !isEtdrsActive,
    });

    if (!etdrsActive) {
      changeTool({}, "Etdrs");
    } else {
      try {
        cornerstoneTools.setToolPassive("Etdrs");
      } catch (_e) {
        /* noop */
      }
      changeTool({}, "MOUSE");
    }

    dispatch({
      type: "etdrs_changed",
      etdrsActive: !etdrsActive,
    });
  };
  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleEtdrsToggle = () => {
    dispatch({
      type: "makeEtdrsActive_changed",
      makeEtdrsActive: false,
    });
    dispatch({
      type: "isEtdrsActive_changed",
      isEtdrsActive: !isEtdrsActive,
    });

    setIsEtdrsActive(!isEtdrsActive); // Toggle the etdrsActive state
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append("file", file);

    // try {
    //   const response = await axios.post(
    //     "http://localhost:5000/api/v1/file-upload",
    //     formData,
    //     {
    //       headers: {
    //         "Content-Type": "multipart/form-data",
    //       },
    //     }
    //   );
    //
    // } catch (error) {
    //
    // }
  };

  React.useEffect(() => {
    if (
      dicom_data.dicom_data[query.get("stack_id")] === undefined ||
      dicom_data.dicom_data[query.get("stack_id")].OP === null ||
      dicom_data.dicom_data[query.get("stack_id")].OPT === null
    ) {
      setImageReceieved(true);
      // setTimeout(() => {
      //   dispatch({
      //     type: "makeEtdrsActive_changed",
      //     makeEtdrsActive: false,
      //   });
      // }, 5000);
    } else {
      setImageReceieved(false);
    }
  }, [dicom_data?.dicom_data[query.get("stack_id")]]);

  React.useEffect(() => {
    if (!isEtdrsActive) {
      dispatch({
        type: "etdrs_changed",
        etdrsActive: false,
      });
    }
  }, [isEtdrsActive]);

  const handleChange = (event) => {
    setChecked(event.target.checked);

    dispatch({
      type: "Change_Lison_Active",
      lisonStatus: event.target.checked,
    });
    dispatch({
      type: "ANNOTATION_CHANGE_REQUEST",
      stack_id: query.get("stack_id"),
      key: "OPT",
      payload: {
        index: 0,
        annot_type: "Lison",
        action_type: "toggle_Lison",
      },
    });
  };

  React.useEffect(() => {
    if (dicom_data?.lisonsAreasData?.finish_time) {
      //   handleChange({ target: { checked: true } });
      const timeout = setTimeout(() => {
        // Code to be executed after 300ms
        handleChange({ target: { checked: true } });
      }, 500);

      // Clear the timeout if the component unmounts or if dependency changes
      return () => clearTimeout(timeout);
    }
  }, [dicom_data.lisonsAreasData]);

  // React.useEffect(() => {
  //   setChecked(dicom_data.LisonActive);
  // }, [dicom_data.LisonActive]);

  const otherTools = [
    "Zoom",
    "Pan",
    "Eraser",
    "AiSegmentationEraser",
    "FluidSegmentationEraser",
  ];
  const dcmData = useSelector((state) => state.dcm);

  React.useEffect(() => {
    if (
      !dcmData.manualFluidTargetLabel &&
      FLUID_MANUAL_TOOLS.has(dcmData.selectedTool)
    ) {
      const passiveName =
        dcmData.selectedTool === "Paint"
          ? "Brush"
          : dcmData.selectedTool === "FluidSegmentationEraser"
            ? "AiSegmentationEraser"
            : dcmData.selectedTool;
      try {
        cornerstoneTools.setToolPassive(passiveName);
      } catch (_e) {
        /* noop */
      }
      dispatch({ type: "CHANGE_SELECTED_TOOL", payload: "MOUSE" });
    }
  }, [dcmData.manualFluidTargetLabel, dcmData.selectedTool]);

  const drawingTools = dcmData.drawingTools;

  const setCornerstoneToolPassiveForSelection = (selectedTool) => {
    const name =
      selectedTool === "Paint"
        ? "Brush"
        : selectedTool === "FluidSegmentationEraser"
          ? "AiSegmentationEraser"
          : selectedTool;
    try {
      cornerstoneTools.setToolPassive(name);
    } catch (_e) {
      /* noop */
    }
  };

  const changeTool = (event, tool_name) => {
    disableJoinedSegmentEditMode();

    if (tool_name === "Etdrs") {
      const Etdrs = cornerstoneTools.Etdrs;
      cornerstoneTools.addTool(Etdrs);
      cornerstoneTools.setToolActive("Etdrs", { mouseButtonMask: 1 });
      dispatch({ type: "CHANGE_SELECTED_TOOL", payload: tool_name });
      return;
    }

    if (tool_name === "AiSegmentationEraser") {
      dispatch({ type: "CHANGE_SELECTED_TOOL", payload: tool_name });
      activateSegmentationEraserForMode(SEGMENTATION_ERASER_MODE.AI);
      return;
    }

    if (tool_name === "FluidSegmentationEraser") {
      dispatch({ type: "CHANGE_SELECTED_TOOL", payload: tool_name });
      activateSegmentationEraserForMode(SEGMENTATION_ERASER_MODE.FLUID);
      return;
    }

    if (tool_name === "Paint") {
      setCornerstoneToolPassiveForSelection(dcmData.selectedTool);
      addTools("Brush");
      try {
        cornerstoneTools.setToolPassive("Brush");
      } catch (_e) {
        /* noop */
      }
      dispatch({ type: "CHANGE_SELECTED_TOOL", payload: "Paint" });
      return;
    }

    if (drawingTools.includes(tool_name) || otherTools.includes(tool_name)) {
      addTools(tool_name);
    } else if (tool_name === null) {
      setCornerstoneToolPassiveForSelection(dcmData.selectedTool);
      dispatch({ type: "CHANGE_SELECTED_TOOL", payload: "MOUSE" });
      return;
    } else {
      setCornerstoneToolPassiveForSelection(dcmData.selectedTool);
    }
    dispatch({ type: "CHANGE_SELECTED_TOOL", payload: tool_name });
  };

  const handleDrawingToolGroupChange = (event, tool_name) => {
    changeTool(event, tool_name);
  };

  const handleFluidToolGroupChange = (event, tool_name) => {
    if (tool_name === "Etdrs" || (tool_name == null && etdrsActive)) {
      handleToggle();
      return;
    }
    if (!tool_name) {
      return;
    }
    changeTool(event, tool_name);
  };

  const fluidToolsValue =
    etdrsActive || dcmData.selectedTool === "Etdrs"
      ? "Etdrs"
      : FLUID_MANUAL_TOOLS.has(dcmData.selectedTool)
        ? dcmData.selectedTool
        : null;

  const currentFrameDirty = Boolean(
    dcmData.fluidDirtyFrames?.[String(dcmData.currentFrame)],
  );
  const hasAnyFluidDirty = Object.values(dcmData.fluidDirtyFrames || {}).some(
    Boolean,
  );

  const handleFluidReset = () => {
    const stackId = query.get("stack_id");
    const stackData = dcmData.dicom_data?.[stackId];
    const imageIds = stackData?.OPT?.stack?.imageIds;
    const frameIndex = dcmData.currentFrame;
    const imageId = imageIds?.[frameIndex];
    if (!imageId) return;

    const ok = resetFluidFrameToBaseline(
      stackId,
      frameIndex,
      imageId,
      dcmData?.settings?.FluidSegmentation,
      dcmData?.settings?.FluidSegmentationColors,
    );
    if (!ok) return;

    dispatch({ type: "CLEAR_FLUID_FRAME_DIRTY", frameIndex });
    const bscanEl = dcmData?.dicomRefBscan?.current;
    if (bscanEl) {
      cornerstone.updateImage(bscanEl);
    }
  };

  const handleFluidSubmit = async () => {
    const stackId = query.get("stack_id");
    const stackData = dcmData.dicom_data?.[stackId];
    const imageIds = stackData?.OPT?.stack?.imageIds;
    try {
      await Etdrs_Volumes_Api_Call(
        dcmData,
        stackId,
        dispatch,
        null,
        buildVolumesSaveParams(query),
      );
      if (imageIds?.length) {
        snapshotFluidBaselinesForStack(
          stackId,
          imageIds,
          dcmData?.activeFluidLabels,
        );
      }
      dispatch({ type: "CLEAR_ALL_FLUID_DIRTY_FRAMES" });
      const opEl = getOpCornerstoneElement(dcmData);
      if (opEl) {
        cornerstone.updateImage(opEl);
      }
      const bscanEl = dcmData?.dicomRefBscan?.current;
      if (bscanEl) {
        cornerstone.updateImage(bscanEl);
      }
    } catch (err) {
      console.error("Fluid volumes submit failed:", err);
      const detail =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unknown error";
      alert(`Failed to submit fluid changes (${detail}). Please try again.`);
    }
  };

  const handleFluidDrawPointsToggle = () => {
    const next = !dcmData.fluidPayloadPointsVisible;
    dispatch({ type: "SET_FLUID_PAYLOAD_POINTS_VISIBLE", visible: next });
    const bscanEl = dcmData?.dicomRefBscan?.current;
    if (bscanEl) {
      cornerstone.updateImage(bscanEl);
    }
  };
  const SaveInputLisonData = (inputData) => {
    dispatch({ type: "Lison_Input_changed", MlLisonsInputData: inputData });
  };

  const OnAreaTransfer = () => {
    dispatch({
      type: "selected_area_Changed",
      SelectedArea: {
        ...dicom_data.SelectedArea,
        status: true,
      },
    });
  };

  const mlDataUpdated = (inputData, outputData) => {
    dispatch({
      type: "mlLesion_Data_changed",
      mlLesionPlayloadData: inputData,
      mlLesionResponseData: outputData,
    });
  };
  const onSubmitClick = async () => {
    dispatch({
      type: "etdrsFallBack_changed",
      etdrsFallBack: false,
    });

    dispatch({
      type: "submitDisabled_changed",
      submitDisable: true,
    });
    if (dicom_data.mainScreenShotUrl !== "") {
      prevSs = dicom_data.mainScreenShotUrl;
    }

    dispatch({
      type: "mainScreenShot_url_changed",
      mainScreenShotUrl: "",
    });
    dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: true });

    setsubmitting(true);
    const stackId = query.get("stack_id");
    const etdrsValues_From_Segmentation_Api = await Etdrs_Segmentation_Api_Call(
      dicom_data,
      stackId,
      dispatch,
    );

    if (etdrsValues_From_Segmentation_Api) {
      const joinedSegmentsFrozen = freezeSubmittedJoinedSegments(
        dicom_data,
        stackId,
      );

      if (joinedSegmentsFrozen) {
        dispatch({ type: "SET_SEGMENTS_TO_DRAW", SegmentsToDraw: {} });
        dispatch({ type: "SET_DRAWN_SEGMENTS", drawnSegments: {} });
        const bscanEl = dicom_data?.dicomRefBscan?.current;
        if (bscanEl) {
          cornerstone.updateImage(bscanEl);
        }
      }
    }

    let submitResponse = await Submit_Lison_annotations(
      dcmData,
      stackId,
      SaveInputLisonData,
      mlDataUpdated,
    );
    dispatch({ type: "Lison_Submit_Status_changed", SubmitLisonsStatus: true });
    dispatch({
      type: "takeScreenShot_changed",
      takeScreenShot: true,
    });
    submitResponse?.area_versions.forEach((area) => {
      // Calculate total area size for each area_type
      const total_area = area.lesions.reduce(
        (total, lesion) => total + lesion.area_size_mm2,
        0,
      );

      // Add total_area property to each area_type object
      area.total_area = total_area;
    });

    const areaTypes = submitResponse?.area_versions.map((obj) => obj.area_type);

    dispatch({ type: "Set_Lisons_Area_Data", lisonsAreasData: submitResponse });
    setsubmitting(false);

    // Now 'areaTypes' is an array containing the "area_type" values

    let options = {
      ...dicom_data.lisonOptions,
    };

    options["lisonAreaOptions"] = areaTypes;
    dispatch({
      type: "Lison_Option_changed",
      lisonOptions: options,
    });

    // const executeScreenshot = async () => {
    //   await captureScreenshot();
    // };

    // setTimeout(() => {
    //   executeScreenshot();
    // }, 3000);

    let etdrsState = null;
    const opElement = getOpCornerstoneElement(dicom_data);
    if (opElement) {
      try {
        cornerstone.getEnabledElement(opElement);
        etdrsState = cornerstoneTools.getToolState(opElement, "Etdrs");
      } catch (_e) {
        etdrsState = null;
      }
    }
    const currentStart = etdrsState?.data[0]?.handles?.start;
    if (currentStart) {
      dispatch({
        type: "SET_PREV_START_REF",
        payload: { ...currentStart },
      });
    }

    // dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });
  };

  const onChangeLisonArea = (event) => {
    let options = {
      ...dicom_data.lisonOptions,
    };

    options[event.target.name] = event.target.value;
    dispatch({
      type: "Lison_Option_changed",
      lisonOptions: options,
    });
  };

  const captureScreenshot = async () => {
    try {
      const dataUrl = await toPng(dicom_data?.OpRef?.current, {
        skipFonts: true,
        cacheBust: true,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const randomNumber = Math.floor(1000 + Math.random() * 9000); // Generates a 4-digit random number

      const file = new File(
        [blob],
        `${dicom_data?.basicMetaData?.study_code}_${dicom_data?.basicMetaData?.subject_code}_${dicom_data?.basicMetaData?.visit_label}_${randomNumber}.png`,
        { type: blob.type },
      );
      let responseFileUplaod = await sendFileUpload(
        query.get("stack_id"),
        file,
      );

      dispatch({
        type: "mainScreenShot_url_changed",
        mainScreenShotUrl: responseFileUplaod.urls[0],
      });
    } catch (error) {}
  };

  const openScreenshotDialog = () => {
    setScreenshotDialogOpen(true);
  };

  const closeScreenshotDialog = () => {
    setScreenshotDialogOpen(false);
  };

  const handleScreenshotDownload = async (screenshotType) => {
    const isAllBscans = screenshotType === SCREENSHOT_TYPES.ALL_BSCANS;

    if (isAllBscans) {
      setScreenshotDialogOpen(false);
      setScreenshotProgress({
        open: true,
        message: "Starting B-scan capture...",
        current: 0,
        total: 0,
        etaSeconds: null,
        phase: "capturing",
      });
    } else {
      setScreenshotDownloading(true);
    }

    setImageDownloadPressed(true);

    try {
      const success = await downloadScreenshotByType(
        screenshotType,
        dicom_data,
        dispatch,
        query.get("stack_id"),
        (progress) => {
          setScreenshotProgress((prev) => ({
            ...prev,
            open: true,
            ...progress,
          }));
        },
      );

      if (!success) {
        alert("Unable to capture screenshot. Please try again.");
        return;
      }

      if (!isAllBscans) {
        setScreenshotDialogOpen(false);
      }
    } catch (error) {
      console.error("Screenshot download failed:", error);
      alert("Screenshot download failed. Please try again.");
    } finally {
      setScreenshotDownloading(false);
      setImageDownloadPressed(false);
      if (isAllBscans) {
        setScreenshotProgress((prev) => ({ ...prev, open: false }));
      }
    }
  };

  const handleDownload = (data, fileName, fileType) => {
    let fileContent;
    let fileMimeType;

    if (fileType === "txt") {
      fileContent = JSON.stringify(data, null, 2);
      fileMimeType = "text/plain";
      fileName += ".txt";
    } else if (fileType === "json") {
      fileContent = JSON.stringify(data, null, 2);
      fileMimeType = "application/json";
      fileName += ".json";
    } else {
      return;
    }

    const blob = new Blob([fileContent], { type: fileMimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDataDownload = async (isInput) => {
    dispatch({
      type: "LoadingOverlay_changed",
      LoadingOverlay: true,
    });

    try {
      let data;
      if (isInput) {
        data = await generateMlPayload(dicom_data, query.get("stack_id"));
      } else {
        data = dicom_data?.mlLesionResponseData;
      }

      const optUrl = dicom_data.dicom_data[query.get("stack_id")].OPT.url;
      const resp = dicom_data?.opMetaData;

      const lateralityValue =
        resp.data.find((item) => item.name === "Laterality")?.Value || "";
      const laterality =
        lateralityValue === "R" ? "OD" : lateralityValue === "L" ? "OS" : "";

      handleDownload(
        data,
        `EZDA_${isInput ? "INPUT" : "OUTPUT"}_DATA_${
          dicom_data?.basicMetaData?.study_code
        }_${dicom_data?.basicMetaData?.subject_code}_${
          dicom_data?.basicMetaData?.visit_label
        }_${laterality}_${new Date().toISOString().split("T")[0]}`,
        "json",
      );
    } catch (err) {
      console.error("Error in data download:", err);
    } finally {
      dispatch({
        type: "LoadingOverlay_changed",
        LoadingOverlay: false,
      });
    }
  };

  useEffect(() => {
    if (
      dicom_data.dicom_data[query.get("stack_id")]?.OP?.annotations?.Etdrs?.data
        .length > 0 &&
      !isEtdrsActive &&
      !etdrsActive &&
      dicom_data.makeEtdrsActive
    ) {
      dispatch({
        type: "makeEtdrsActive_changed",
        makeEtdrsActive: false,
      });

      handleEtdrsToggle();
      handleToggle();
    }
  }, [dicom_data.dicom_data[query.get("stack_id")]?.OP]);

  // NOTE: This effect handles browser tab visibility changes.
  // When the user switches away from the tab during processing, it pauses screenshots
  // by setting takeScreenShot to false; when they return, it restores it to true.
  // Add this useEffect to handle tab switching warning
  const visibilityChangeDebounceRef = React.useRef(null);
  useEffect(() => {
    // Handle tab visibility change
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === "hidden" &&
        (submiting || dicom_data.SubmitLisonsStatus)
      ) {
        // User switched away from the tab - set takeScreenShot to false

        dispatch({
          type: "takeScreenShot_changed",
          takeScreenShot: false,
        });
      } else if (
        document.visibilityState === "visible" &&
        !dicom_data.takeScreenShot
      ) {
        // dispatch({
        //   type: "mainScreenShot_url_changed",
        //   mainScreenShotUrl: "",
        // });
        // User returned to the tab - restore takeScreenShot to true if it was false
        // Ensure OPViewer's screenshot effect can fire by clearing submit status gate
        dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: true });
        if (visibilityChangeDebounceRef.current) {
          clearTimeout(visibilityChangeDebounceRef.current);
        }
        visibilityChangeDebounceRef.current = setTimeout(async () => {
          await captureScreenshot();
          dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
          visibilityChangeDebounceRef.current = null;
        }, 2000);
      }
    };

    // Add event listeners when processing starts
    if (submiting || dicom_data.SubmitLisonsStatus) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    // // Clean up event listeners
    // return () => {
    //   document.removeEventListener("visibilitychange", handleVisibilityChange);
    // };
  }, [submiting, dicom_data.SubmitLisonsStatus, dicom_data.takeScreenShot]);

  // When screenshot completes (URL set) after tab-return trigger, hide overlay
  useEffect(() => {
    if (tabRestoreScreenshot && dicom_data?.mainScreenShotUrl) {
      dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });
      setTabRestoreScreenshot(false);
    }
  }, [dicom_data?.mainScreenShotUrl, tabRestoreScreenshot]);

  return (
    <>
      <EtdrsConfirmationModal
        open={etdrsConfirm}
        handleClose={() => {
          dispatch({
            type: "etdrsFallBack_changed",
            etdrsFallBack: true,
          });
          setEtdrsConfirm(false);
        }}
        handleProcessSubmit={onSubmitClick}
      />
      <ScreenshotDownloadDialog
        open={screenshotDialogOpen}
        onClose={closeScreenshotDialog}
        onDownload={handleScreenshotDownload}
        downloading={screenshotDownloading}
      />
      <ScreenshotProgressSnackbar
        open={screenshotProgress.open}
        progress={screenshotProgress}
      />
      {dicom_data.LoadingOverlay || dicom_data.fluidVolumesSubmitting ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999, // Adjust the z-index as needed
          }}
        >
          {dicom_data?.displayScreenShotUrl && dicom_data?.screenShotDisplay ? (
            <>
              <img
                src={dicom_data?.displayScreenShotUrl}
                alt="Image Clicked"
                style={{
                  width: "70vw", // 70% of viewport width
                  height: "90vh", // 80% of viewport height
                  objectFit: "contain", // Maintains aspect ratio while fitting within dimensions
                }}
              />
              <button
                onClick={() => {
                  dispatch({
                    type: "LoadingOverlay_changed",
                    LoadingOverlay: false,
                  });
                  dispatch({
                    type: "screenShot_Display_changed",
                    screenShotDisplay: false,
                  });
                }}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  borderRadius: "50%",
                  backgroundColor: "black",
                  color: "white",
                  width: "30px",
                  height: "30px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                X
              </button>
            </>
          ) : (
            <CircularProgress color="primary" />
          )}
        </div>
      ) : null}

      <div style={{ display: "flex", color: "#9C27B0" }}>
        <ToggleButtonGroup
          disabled={imageReceieved}
          style={{ height: "100%" }}
          value={dcmData.selectedTool}
          color="secondary"
          exclusive
          onChange={handleDrawingToolGroupChange}
          aria-label="text alignment"
        >
          <ToggleButton value="Line" aria-label="Line Tool">
            <Tooltip title="Line Tool">
              <MovingIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="Probe" aria-label="Probe Tool">
            <Tooltip title="Probe Tool">
              <LensIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="EllipticalRoi" aria-label="Ellipse Tool">
            <Tooltip title="Ellipse Tool">
              <TbOvalVertical fontSize="1.5rem" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="CircleRoi" aria-label="Circle Tool">
            <Tooltip title="Circle Tool">
              <PanoramaFishEyeIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="Length" aria-label="Length Tool">
            <Tooltip title="Length Tool">
              <CallMade />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="Angle" aria-label="Angle Tool">
            <Tooltip title="Angle Tool">
              <SubdirectoryArrowRightIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="RectangleRoi" aria-label="Rectangle Tool">
            <Tooltip title="Rectangle Tool">
              <CropSquareIcon />
            </Tooltip>
          </ToggleButton>

          <ToggleButton value="DragRL" aria-label="Drag Left Right">
            <Tooltip title="Drag Left Right">
              <AlignHorizontalLeftIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="ThicknessMap" aria-label="ThicknessMap">
            <Tooltip title="ThicknessMap">
              <AddRoadIcon />
            </Tooltip>
          </ToggleButton>

          <ToggleButton value="MOUSE" aria-label="Mouse">
            <Tooltip title="Mouse">
              <MouseIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="AiSegmentationEraser"
            aria-label="AI segmentation eraser"
            disabled={imageReceieved}
          >
            <Tooltip title="AI Segmentation eraser — Ctrl + mouse wheel to resize">
              <AiSegmentationEraserIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginLeft: "auto",
            marginRight: 8,
            paddingLeft: 8,
            borderLeft: "2px solid #9C27B0",
            alignSelf: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#9C27B0",
              fontWeight: 600,
              fontSize: "0.65rem",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            Fluid Tools
          </Typography>
          <ToggleButtonGroup
            disabled={imageReceieved}
            size="small"
            value={fluidToolsValue}
            color="secondary"
            exclusive
            onChange={handleFluidToolGroupChange}
            aria-label="fluid tools"
            sx={{
              "& .MuiToggleButton-root": FLUID_TOOL_BUTTON_SX,
              "& .MuiSvgIcon-root": { fontSize: "1.05rem" },
            }}
          >
            <ToggleButton
              value="Paint"
              aria-label="Fluid paint"
              disabled={!dcmData.manualFluidTargetLabel}
            >
              <Tooltip
                title={
                  !dcmData.manualFluidTargetLabel
                    ? "Select IRF / SRF / PED / SHRM under Annotations first"
                    : "Fluid paint — stroke or fill (see Annotations panel)"
                }
              >
                <FormatPaintIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="FluidSegmentationEraser"
              aria-label="Fluid segmentation eraser"
              disabled={!dcmData.manualFluidTargetLabel}
            >
              <Tooltip
                title={
                  !dcmData.manualFluidTargetLabel
                    ? "Select IRF / SRF / PED / SHRM under Annotations first"
                    : "Fluid eraser — Ctrl + mouse wheel to resize"
                }
              >
                <FluidEraserIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="Etdrs" aria-label="Etdrs" id="etdrs-circles">
              <Tooltip title="ETDRS grid">
                <FiTarget
                  fontSize="1.05rem"
                  style={{ color: etdrsActive ? "red" : "#00000089" }}
                />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip
            title={
              currentFrameDirty
                ? "Reset fluid on this frame to API defaults"
                : "No fluid edits on this frame"
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={imageReceieved || !currentFrameDirty}
                onClick={handleFluidReset}
                aria-label="Reset fluid frame"
                sx={{ ml: 0.5, p: 0.4 }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              hasAnyFluidDirty
                ? "Submit fluid changes for all frames (mat_compute_volume)"
                : "No fluid edits to submit"
            }
          >
            <span>
              <IconButton
                size="small"
                color="secondary"
                disabled={
                  imageReceieved ||
                  !hasAnyFluidDirty ||
                  dcmData.fluidVolumesSubmitting
                }
                onClick={handleFluidSubmit}
                aria-label="Submit fluid changes"
                sx={{ p: 0.4 }}
              >
                {dcmData.fluidVolumesSubmitting ? (
                  <CircularProgress size={18} />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          {query.get("me") === "dev" ? (
            <>
              <Tooltip
                title={
                  dcmData.fluidPayloadPointsVisible
                    ? "Hide API payload line endpoints"
                    : "Draw API payload line endpoints on current B-scan"
                }
              >
                <IconButton
                  size="small"
                  color={
                    dcmData.fluidPayloadPointsVisible ? "secondary" : "default"
                  }
                  disabled={imageReceieved}
                  onClick={handleFluidDrawPointsToggle}
                  aria-label="Draw payload points"
                  sx={{ p: 0.4 }}
                >
                  <ScatterPlotIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{
                  color: "#9C27B0",
                  fontSize: "0.62rem",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  ml: 0.25,
                }}
              >
                Draw Points
              </Typography>
            </>
          ) : null}
        </div>
        <ToggleButton
          value="JoinSegments"
          aria-label="Join Erased Segments"
          style={{
            display:
              Object.values(dicom_data.SegmentsToDraw || {}).some(
                (segments) => segments?.length > 0,
              )
                ? "block"
                : "none",
          }}
          onClick={joinErasedSegments}
        >
          <Tooltip title="Join Erased Segments">
            <span
              style={{
                fontFamily: '"Material Symbols Outlined"',
                fontWeight: "normal",
                fontStyle: "normal",
                fontSize: "24px",
                lineHeight: 1,
                letterSpacing: "normal",
                textTransform: "none",
                display: "inline-block",
                whiteSpace: "nowrap",
                wordWrap: "normal",
                direction: "ltr",
              }}
            >
              diagonal_line
            </span>
          </Tooltip>
        </ToggleButton>

        <ToggleButtonGroup
          disabled={imageDownloadPressed || imageReceieved}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            paddingInline: "10px",
          }}
        >
          <Tooltip title="Screenshot Download">
            <ToggleButton value="screenshot" onClick={openScreenshotDialog}>
              <ScreenshotMonitorIcon
                sx={{
                  color:
                    imageDownloadPressed || imageReceieved ? "gray" : "#7B1FA2",
                }}
              />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        {!imageReceieved ? <BrushToolSettings /> : null}

        <Switch
          id="lesion-toggle"
          disabled={imageReceieved}
          checked={checked}
          onChange={handleChange}
          inputProps={{ "aria-label": "controlled" }}
        />
        {checked ? (
          <>
            <ToggleButtonGroup
              disabled={imageReceieved}
              style={{ height: "100%" }}
              value={dcmData.selectedTool}
              color="secondary"
              exclusive
              onChange={handleDrawingToolGroupChange}
              aria-label="text alignment"
            >
              <ToggleButton
                value="Lison"
                aria-label="Lison Tool"
                disabled={!checked}
              >
                <Tooltip title="Lesion Tool">
                  <SettingsEthernetIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        ) : null}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Button
            disabled={imageReceieved}
            id="EzdaProcess"
            variant="contained"
            size="small"
            style={{ marginLeft: 10, display: checked ? "flex" : "none" }}
            color={
              dicom_data?.MlLisonsInputsSubmitStatus ? "error" : "secondary"
            }
            onClick={() => {
              if (!etdrsActive && isEtdrsActive) {
                changeTool({}, "MOUSE");
                handleToggle();
              }
              if (isEtdrsActive) {
                setEtdrsConfirm(true);
                dispatch({
                  type: "etdrs_changed",
                  etdrsActive: true,
                });
              } else {
                onSubmitClick();
              }
            }}
          >
            {!submiting && !dicom_data.SubmitLisonsStatus
              ? "Process"
              : "Processing"}
          </Button>

          {/* <p style={{ color: "red" }}>Please submit again for latest lisons.</p> */}
        </div>
        {query.get("Option") === "JsonIO" ? (
          <Button
            variant="contained"
            size="small"
            onClick={handleDownloadAiSegmentation}
          >
            Download AI Segmentation payload
          </Button>
        ) : null}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            // Optional, to center vertically as well
          }}
        >
          {/* {dicom_data?.lisonOptions?.lisonAreaOptions.length > 1 && checked ? (
            <>
              <FormControl style={{ marginLeft: 25, display: "flex" }}>
                <InputLabel id="lisonAreaselected">Area</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  disabled={true}
                  value={dicom_data.lisonOptions.lisonAreaselected}
                  label="Age"
                  onChange={onChangeLisonArea}
                  name="lisonAreaselected"
                  sx={{
                    height: 30, // Adjust this value to your desired height
                    width: 165, // Adjust this value to your desired width
                  }}
                >
                  {dicom_data.lisonOptions.lisonAreaOptions.map(
                    (lisonAreaoption) => {
                      return (
                        <MenuItem value={lisonAreaoption}>
                          {lisonAreaoption}
                        </MenuItem>
                      );
                    }
                  )}
                </Select>
              </FormControl>
              <FormControl style={{ marginLeft: 25 }}>
                <InputLabel id="lisonDisplaySelected">Display</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={dicom_data.lisonOptions.lisonDisplaySelected}
                  label="Display"
                  name="lisonDisplaySelected"
                  onChange={onChangeLisonArea}
                  sx={{
                    height: 30, // Adjust this value to your desired height
                    width: 135, // Adjust this value to your desired width
                  }}
                >
                  {dicom_data.lisonOptions.lisonDisplayOptions.map(
                    (lisonDisplayoption) => {
                      return (
                        <MenuItem value={lisonDisplayoption}>
                          {lisonDisplayoption}
                        </MenuItem>
                      );
                    }
                  )}
                </Select>
              </FormControl>
            </>
          ) : null} */}
        </div>

        {checked && query.get("Option") === "JsonIO" ? (
          <>
            <div
              style={{
                marginLeft: 10,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                // disabled={
                //   !dicom_data?.mlLesionPlayloadData ||
                //   !dicom_data?.mlLesionResponseData
                // }
                variant="contained"
                size="small"
                onClick={() => handleDataDownload(true)}
              >
                Input
              </button>

              <button
                disabled={!dicom_data?.mlLesionResponseData}
                variant="contained"
                size="small"
                style={{ marginLeft: 10 }}
                onClick={() => handleDataDownload(false)}
              >
                Output
              </button>
            </div>
          </>
        ) : null}

        {/* <form onSubmit={handleSubmit}>
          <input type="file" onChange={handleFileChange} />
          <button type="submit">Upload</button>
        </form> */}
      </div>
    </>
  );
}
